/**
 * Repository Pattern Implementation
 * 
 * This module exports all repository classes and types.
 * Repositories provide a clean abstraction layer for database access.
 * 
 * Usage:
 * ```typescript
 * import { CustomerRepository, repositories } from '@/lib/repositories';
 * 
 * // Use specific repository
 * const customerRepo = new CustomerRepository();
 * const customers = await customerRepo.findAll();
 * 
 * // Or use singleton instances
 * const customers = await repositories.customers.findAll();
 * ```
 */

// Export base repository and types
export * from './BaseRepository';

// Export customer repository and types
export * from './CustomerRepository';

// Export agreement repository and types
export * from './AgreementRepository';

// Export call repository and types
export * from './CallRepository';

// Export knowledge repository and types
export * from './KnowledgeRepository';

// Export equipment repository and types
export * from './EquipmentRepository';

// Export queue repository and types
export * from './QueueRepository';

// Export agent repository and types
export * from './AgentRepository';

// Export company config repository and types
export * from './CompanyConfigRepository';

// Export AI call repository and types
export * from './AICallRepository';

// Export SuccessWare repository and types
export * from './SuccessWareRepository';
export * from './MigrationRepository';
export * from './UserRepository';

// Singleton instances for convenience
// These are created once and reused throughout the application
import { CustomerRepository } from './CustomerRepository';
import { AgreementRepository } from './AgreementRepository';
import { CallRepository } from './CallRepository';
import { KnowledgeRepository } from './KnowledgeRepository';
import { EquipmentRepository } from './EquipmentRepository';
import { QueueRepository } from './QueueRepository';
import { AgentRepository } from './AgentRepository';
import { CompanyConfigRepository } from './CompanyConfigRepository';
import { AICallRepository } from './AICallRepository';
import { SuccessWareRepository } from './SuccessWareRepository';
import { UserRepository } from './UserRepository';
import { MigrationRepository } from './MigrationRepository';

/**
 * Singleton repository instances
 * 
 * These instances are created once and can be imported anywhere
 * in the application for consistent data access.
 * 
 * @example
 * ```typescript
 * import { repositories } from '@/lib/repositories';
 * 
 * const customers = await repositories.customers.findAll();
 * const stats = await repositories.customers.getStatistics();
 * ```
 */
export const repositories = {
  customers: new CustomerRepository(),
  agreements: new AgreementRepository(),
  calls: new CallRepository(),
  knowledge: new KnowledgeRepository(),
  equipment: new EquipmentRepository(),
  queues: new QueueRepository(),
  agents: new AgentRepository(),
  companyConfigs: new CompanyConfigRepository(),
  aiCalls: new AICallRepository(),
  successWare: new SuccessWareRepository(),
  users: new UserRepository(),
  migration: new MigrationRepository(),
};

/**
 * Type helper to get the type of all repositories
 */
export type Repositories = typeof repositories;

/**
 * Type helper to get repository names
 */
export type RepositoryName = keyof Repositories;
